/**
 * Month grid maths for the in-app calendar.
 *
 * Pure and UTC-only: a `YYYY-MM-DD` is a calendar day, not an instant, so every computation
 * here goes through `Date.UTC` and never through the device's local time. Reading a day key
 * with `new Date('2026-08-01')` and asking for `getDate()` would answer 31 in Vietnam.
 *
 * The week starts on Monday, as it does on Vietnamese calendars.
 */
export type YearMonth = Readonly<{ year: number; month: number }>;

export type DayCell = Readonly<{
  dayKey: string;
  day: number;
  /** False for the leading/trailing days that belong to the neighbouring months. */
  inMonth: boolean;
}>;

export const WEEKDAY_LABELS = ['T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'CN'] as const;

/** Six weeks always: the grid keeps its height, so the panel never jumps between months. */
const GRID_CELLS = 42;

export function toDayKey(year: number, month: number, day: number): string {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${String(year).padStart(4, '0')}-${pad(month)}-${pad(day)}`;
}

export function parseDayKey(dayKey: string): YearMonth & { day: number } {
  const [year, month, day] = dayKey.split('-').map(Number);
  return { year: year ?? 1970, month: month ?? 1, day: day ?? 1 };
}

export function monthOf(dayKey: string): YearMonth {
  const { year, month } = parseDayKey(dayKey);
  return { year, month };
}

export function addMonths(yearMonth: YearMonth, delta: number): YearMonth {
  // Date.UTC normalises month 0 and 13 into the neighbouring years for us.
  const shifted = new Date(Date.UTC(yearMonth.year, yearMonth.month - 1 + delta, 1));
  return { year: shifted.getUTCFullYear(), month: shifted.getUTCMonth() + 1 };
}

/** The day before a day key — used for the "Hôm qua" shortcut. */
export function previousDay(dayKey: string): string {
  const { year, month, day } = parseDayKey(dayKey);
  const shifted = new Date(Date.UTC(year, month - 1, day - 1));
  return shifted.toISOString().slice(0, 10);
}

const MONTH_YEAR_FORMATTER = new Intl.DateTimeFormat('vi-VN', {
  month: 'long',
  year: 'numeric',
  timeZone: 'UTC',
});

/** "tháng 8 năm 2026" — the panel heading. */
export function monthLabel(yearMonth: YearMonth): string {
  return MONTH_YEAR_FORMATTER.format(Date.UTC(yearMonth.year, yearMonth.month - 1, 1));
}

const FULL_DATE_FORMATTER = new Intl.DateTimeFormat('vi-VN', {
  weekday: 'long',
  day: 'numeric',
  month: 'numeric',
  year: 'numeric',
  timeZone: 'UTC',
});

/** The accessible name of a day button: "thứ Hai, 10 tháng 8, 2026". */
export function fullDateLabel(dayKey: string): string {
  return FULL_DATE_FORMATTER.format(new Date(`${dayKey}T00:00:00Z`));
}

/**
 * The 42 cells of a month grid, Monday first, including the neighbouring days that fill the
 * first and last weeks.
 */
export function monthGrid(yearMonth: YearMonth): readonly DayCell[] {
  const firstOfMonth = new Date(Date.UTC(yearMonth.year, yearMonth.month - 1, 1));
  // getUTCDay() is Sunday-first; shift it so Monday is 0.
  const leading = (firstOfMonth.getUTCDay() + 6) % 7;

  const cells: DayCell[] = [];
  for (let index = 0; index < GRID_CELLS; index += 1) {
    const date = new Date(Date.UTC(yearMonth.year, yearMonth.month - 1, 1 - leading + index));
    cells.push({
      dayKey: date.toISOString().slice(0, 10),
      day: date.getUTCDate(),
      inMonth: date.getUTCMonth() === yearMonth.month - 1,
    });
  }
  return cells;
}
